import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useStoreState, useStoreActions } from "../../../store";

import {
  ModalOverlay,
  ModalBox,
  Header,
  CloseButton,
  Body,
  Form,
  Field,
  SelectWrapper,
  FieldLabel,
  Select,
  Input,
  Textarea,
  SubmitButton,
  SubmitRow,
  Divider
} from "./DiagnosticModal.styles";

import ARROW_SRC from "../../../Icons/Arrow.svg";
import X_SRC from "../../../Icons/X.svg";

import { submitFeedback } from "../../../services/ApiService";
import { uploadDocument } from "../../../services/ApiService";
import { FeedbackSubmission } from "../../../types/FeedbackSubmission";
import { useAuth } from "../../../utility/useAuth";
import { logger } from "../../../utility/logger";
import { notify } from "../../../utility/notify";
import { findErrorCode } from "../../../utility/errorCatalog";

// The recipient and sender used to be declared here and were already decorative — the Function
// App overrides both from its own FromEmail/ToEmail app settings, and must, because the ACS
// sender domain differs per region. The HTML body moved server-side with them: it is now built
// by FeedbackEmailBuilder, which also escapes the subject and description this file used to
// interpolate raw.

interface DiagnosticModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Optional starting text for the Subject / Description fields, seeded when the modal
   * OPENS. Added for the support assistant, which hands over the question it could not
   * answer plus the conversation so far.
   *
   * The user still reviews and submits — this pre-fills, it never sends. It is also only a
   * starting point: whatever they type over it wins, and it is not re-applied while the
   * modal stays open.
   */
  initialSubject?: string;
  initialDescription?: string;
  /**
   * Context for an escalation that started at a REPORTED FAILURE
   * (`ReportedError.diagnostics` from `utility/reportError`): the code, the operation, the
   * production order, and the endpoint + HTTP status.
   *
   * It rides in the E-MAIL, never in the Description box. Two reasons, and both matter:
   * the box is the user's own words and they must be able to rewrite them without deleting
   * the only copy of the detail support will ask for; and this carries the HTTP detail,
   * which is exactly what the support prompt forbids the model from being shown. The
   * e-mail is a human destination, so it is the one place it belongs.
   */
  diagnostics?: string;
  onSubmitFeedback?: (payload: {
    category: string;
    subject: string;
    description: string;
    priority?: string;
    productionOrder?: string;
  }) => void;
}

const PRIORITY_OPTIONS = [
  { value: "2", label: "High" },
  { value: "3", label: "Moderate" },
  { value: "4", label: "Low" },
];

const DESCRIPTION_PLACEHOLDER =
  "Please provide details, e.g., are you working in CSM or SAP B1?  " +
  "Which screen are you viewing?  Describe what you were doing when you " +
  "encountered the issue.";

const getTimestampYYYYDDMMHHMMSSmmm = () => {
  const d = new Date();

  const pad = (n: number, len = 2) => String(n).padStart(len, "0");

  return (
    d.getUTCFullYear().toString() +
    pad(d.getUTCDate()) +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    pad(d.getUTCMilliseconds(), 3)
  );
};



/**
 * Pre-fill a drawer that is ALREADY OPEN, instead of opening a second one.
 *
 * The drawer has two independent hosts (the footer, and the support assistant's escalation
 * instance). A thumbs-down in CoSMo while the footer's drawer was up used to open the
 * assistant's instance on top of it — two drawers, and closing either one cleared the store's
 * `isDiagnosticPaneOpen` while the other was still showing. The open drawer registers here, and
 * the assistant hands its report over instead of opening its own.
 *
 * ⚠️ IT MERGES, IT DOES NOT REPLACE. Whatever the user already typed stays: an empty Subject
 * takes the seed, a non-empty one is left alone, and the seeded Description goes BELOW the
 * user's own text. A category they already picked is kept.
 *
 * Returns `false` when no drawer is open — the caller then opens its own.
 */
export interface ReportPrefill {
  subject: string;
  description: string;
}

let openReportSlot: ((seed: ReportPrefill) => void) | null = null;

export function prefillOpenReport(seed: ReportPrefill): boolean {
  if (!openReportSlot) return false;
  openReportSlot(seed);
  return true;
}

const mintSubmissionId = () =>
  `${crypto.randomUUID()}${getTimestampYYYYDDMMHHMMSSmmm()}`;

const DiagnosticModal: React.FC<DiagnosticModalProps> = ({
  open,
  onClose,
  onSubmitFeedback,
  initialSubject,
  initialDescription,
  diagnostics,
}) => {
  const user = useStoreState((state) => state.user);
  const auth = useAuth();

  const setIsDiagnosticPaneOpen = useStoreActions(
    (a) => a.appSettings.setIsDiagnosticPaneOpen
  );

  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("");
  const [productionOrder, setProductionOrder] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [sending, setSending] = useState(false);

  /**
   * The blob folder for this submission's attachments, and the id sent to the server.
   *
   * ⚠️ IT MUST BE STABLE FOR THE LIFE OF THE OPEN DRAWER. It used to be a plain `const`
   * computed in the component body, so it was RE-MINTED ON EVERY RENDER: the folder the
   * attachments uploaded to and the id the server was told about were the same value only when
   * no render happened in between. Any render during submit — a state update, a store change —
   * silently orphaned the uploads, and the symptom was a ticket with no screenshots rather than
   * an error.
   *
   * A ref rather than state because nothing renders from it, and re-minted on close so the next
   * report gets its own folder.
   */
  const submissionIdRef = useRef<string>("");

  useEffect(() => {
    submissionIdRef.current = open ? mintSubmissionId() : "";
  }, [open]);

  const isBugCategory = category === "bug";

  // Priority and production order only apply to Bug/Support, so drop whatever
  // was typed if the user switches back to Feedback/Enhancement.
  useEffect(() => {
    if (!isBugCategory) {
      setPriority("");
      setProductionOrder("");
    }
  }, [isBugCategory]);

  // Seed the pre-filled text when the modal OPENS, not on every render: the user has to be
  // able to type over it. Keyed on `open` alone for the same reason — if the caller's props
  // change while the modal is up (the assistant answering another question behind it), the
  // half-written report in front of the user must not be overwritten.
  //
  // The category is pre-selected to Bug/Support only when the assistant handed something
  // over (i.e. there is pre-filled text). Opening the drawer straight from the footer must
  // still start blank — pre-picking a category there would put words in the user's mouth.
  useEffect(() => {
    if (!open) return;
    if (initialSubject) setSubject(initialSubject);
    if (initialDescription) setDescription(initialDescription);
    if (initialSubject || initialDescription) setCategory("bug");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // While open, this drawer is where `prefillOpenReport` lands. The cleanup clears the slot only
  // if it is still ours, so one drawer closing cannot unregister another.
  useEffect(() => {
    if (!open) return;

    const apply = (seed: ReportPrefill) => {
      setSubject((s) => (s.trim() ? s : seed.subject));
      setDescription((d) => (d.trim() ? `${d.trimEnd()}\n\n${seed.description}` : seed.description));
      setCategory((c) => c || "bug");
    };

    openReportSlot = apply;

    return () => {
      if (openReportSlot === apply) openReportSlot = null;
    };
  }, [open]);

  /**
   * Publish "the drawer is on screen" to the store.
   *
   * Two things read it, and the drawer is rendered from two independent places — the footer and
   * the assistant's own escalation instance — so a prop could not carry it. This component is
   * the single writer.
   *
   * ⚠️ IT OPENS THE SUPPORT ASSISTANT NOW, not just moves it. `SupportAssistant` watches this
   * flag for a false→true transition and opens its panel, so somebody heading for Submit
   * Feedback is offered the assistant first; the panel also steps out from under this drawer's
   * full-viewport overlay while it is set. It used to decide whether the chat LAUNCHER existed
   * at all for an operator — that rule went when the launcher moved into the app header.
   *
   * The cleanup runs on close AND on unmount, which matters: the footer unmounts on the
   * /diagnostic route, and a flag left stuck at true there would leave the panel shoved sideways
   * with no drawer beside it — and would swallow the NEXT open, which is the transition the
   * assistant is watching for.
   */
  useEffect(() => {
    setIsDiagnosticPaneOpen(open);

    return () => setIsDiagnosticPaneOpen(false);
  }, [open, setIsDiagnosticPaneOpen]);

  const isSubmitDisabled =
    !category ||
    !subject ||
    !description ||
    (isBugCategory && !priority) ||
    sending;

  /* ---------------- Attachments ---------------- */

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    setAttachments(Array.from(files));
  };

  const removeAttachment = (indexToRemove: number) => {
  setAttachments((prev) =>
    prev.filter((_, index) => index !== indexToRemove)
  );
};


  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  /* ---------------- Submit ---------------- */

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitDisabled) return;

    setSending(true);

    // Read ONCE, and reuse for both the upload folder and the payload — that identity is the
    // whole point of the ref above.
    const submissionId = submissionIdRef.current || mintSubmissionId();
    submissionIdRef.current = submissionId;

    try {
      // ✅ Upload attachments using GUID partitioning
      if (attachments.length > 0) {
        await Promise.all(
          attachments.map((file) =>
            uploadDocument(
              auth,
              submissionId,     // folder = GUID
              file.name,
              file,
              user.info.whoto
            )
          )
        );
      }

      onSubmitFeedback?.({
        category,
        subject,
        description,
        priority,
        productionOrder,
      });

      // Prefer the split first/last name, but fall back to the full display name
      // (and finally the backend user record) so the associate is never blank if
      // the IdP omits given_name/family_name.
      const associateName =
        `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() ||
        user.fullName ||
        user.info?.name ||
        "";
      const associateEmail = user.email || user.info?.email || "";

      // The code the report is ABOUT, pulled out of what is actually on screen rather
      // than out of a prop — the common path is a user pasting the toast or reading the
      // code down the phone, and neither goes through `diagnostics`.
      //
      // It now rides at the HEAD of the diagnostics block rather than in its own email rows:
      // the body is built server-side and the backend has no error catalogue, so this is the
      // channel that carries it — and since the ServiceNow incident is opened from that same
      // email body, it reaches the incident too, which the rows never did.
      const errorCode =
        findErrorCode(diagnostics) ?? findErrorCode(`${subject}\n${description}`);

      const diagnosticsBlock = [
        errorCode
          ? `CSM Error Code: ${errorCode.code}\nWhat was being done: ${errorCode.operation}`
          : "",
        diagnostics ?? "",
      ]
        .filter(Boolean)
        .join("\n\n");

      const payload: FeedbackSubmission = {
        category,
        subject,
        description,
        priority: isBugCategory ? priority : undefined,
        productionOrder: isBugCategory
          ? productionOrder.trim() || undefined
          : undefined,
        submissionId,
        whoto: user.info.whoto,
        associateName,
        associateEmail,
        diagnostics: diagnosticsBlock || undefined,
      };

      const result = await submitFeedback(auth, payload);

      // ⚠️ Keyed on emailSent, NOT on the response being truthy. `sendEmail` returned the whole
      // axios response, which is always truthy, so the else branch below was dead code and a
      // failed send reported success. A ticketing outage is deliberately NOT a failure here: the
      // email is the guaranteed record, so the submission stands.
      if (result?.emailSent) {
        notify.success(
          result.ticketNumber
            ? `Feedback submitted — ${result.ticketNumber}`
            : "Feedback submitted successfully!"
        );
        setCategory("");
        setPriority("");
        setProductionOrder("");
        setSubject("");
        setDescription("");
        setAttachments([]);
        onClose();
      } else {
        notify.error("Failed to send feedback. Please try again.");
      }
    } catch (err) {
      logger.error("Submission failed", err);
      notify.error("An error occurred while submitting feedback.");
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <ModalOverlay>
      <ModalBox className={open ? "open" : ""}>
        <Header>
          <div />
          <CloseButton onClick={onClose}>
            <img src={X_SRC} alt="close" />
          </CloseButton>
        </Header>

        <Body>
          <Form onSubmit={handleSubmit}>
            {/* Category */}
            <Field>
              <FieldLabel>
                Please select a feedback category<span className="required">*</span>
              </FieldLabel>
              <SelectWrapper>
                <Select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  required
                >
                  <option value="" disabled>Select a category</option>
                  <option value="bug">Bug/Support</option>
                  <option value="feature">Feedback/Enhancement</option>
                </Select>
               <img src={ARROW_SRC} alt="close" />
              </SelectWrapper>
              <Divider />
            </Field>

            {isBugCategory && (
              <Field>
                <FieldLabel>
                  Priority<span className="required">*</span>
                </FieldLabel>
                <SelectWrapper>
                  <Select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    required
                  >
                    <option value="" disabled>Select a priority</option>
                    {PRIORITY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                  <img src={ARROW_SRC} alt="close" />
                </SelectWrapper>
                <Divider />
              </Field>
            )}

            {/* Production Order (PW) # — optional, Bug/Support only */}
            {isBugCategory && (
              <Field>
                <FieldLabel>Production Order (PW) #</FieldLabel>
                <Input
                  value={productionOrder}
                  onChange={(e) => setProductionOrder(e.target.value)}
                  placeholder="Optional"
                />
              </Field>
            )}

            {/* Subject */}
            <Field>
              <FieldLabel>
                Subject<span className="required">*</span>
              </FieldLabel>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
                placeholder="What is your feedback about?"
              />
            </Field>

            {/* Description */}
            <Field style={{ height: "248px" }}>
              <FieldLabel>
                Description<span className="required">*</span>
              </FieldLabel>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                placeholder={DESCRIPTION_PLACEHOLDER}
              />
            </Field>

            {/* Attachments */}
            <Field>
              <FieldLabel>Add attachments</FieldLabel>

              {attachments.map((file, index) => (
  <div
    key={`${file.name}-${index}`}
    style={{
      background: "#F2F2F2",
      borderRadius: 4,
      padding: "8px 16px 8px 8px",
      display: "flex",
      alignItems: "center",
      gap: 12,
      marginBottom: 8,
      outline: "1px rgba(196,199,200,0.6) solid",
    }}
  >
    {/* Remove (X) Button */}
    <img
      src={X_SRC}
      alt="Remove attachment"
      onClick={() => removeAttachment(index)}
      style={{
        width: 12,
        height: 12,
        cursor: "pointer",
      }}
    />

    {/* File name */}
    <div
      style={{
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        flex: 1,
      }}
    >
      {file.name}
    </div>
  </div>
))}

              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop}
                style={{
                  background: "#F9F9F9",
                  borderRadius: 4,
                  padding: 16,
                  textAlign: "center",
                  outline: "1px rgba(196,199,200,0.6) solid",
                }}
              >
                <input
                  type="file"
                  multiple
                  hidden
                  id="fileInput"
                  onChange={(e) => handleFiles(e.target.files)}
                />
                Drag and drop file(s) or{" "}
                <label
                  htmlFor="fileInput"
                  style={{
                    color: "#004D9E",
                    fontWeight: 500,
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                >
                  browse
                </label>
              </div>
            </Field>

            <SubmitRow>
              <SubmitButton
                type="submit"
                disabled={isSubmitDisabled}
                className={isSubmitDisabled ? "disabled" : ""}
              >
                {sending ? "Sending..." : "Submit"}
              </SubmitButton>
            </SubmitRow>
          </Form>
        </Body>
      </ModalBox>
    </ModalOverlay>,
    document.body
  );
};

export default DiagnosticModal;